import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import {
  NewspaperIcon,
  SparklesIcon,
  DevicePhoneMobileIcon,
  EnvelopeIcon,
  ArrowLeftIcon,
  TrashIcon,
  EyeSlashIcon,
  PhotoIcon,
  XMarkIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  BoldIcon,
  LinkIcon,
  ArrowDownTrayIcon,
  ClipboardDocumentIcon,
} from '@heroicons/react/24/outline'
import api from '../../api/axios'
import {
  getNews, getNewsMeta, createNews, updateNews, publishNews, unpublishNews, deleteNews, sendTestNews, estimateReach,
  uploadNewsImage, aiWriteNews, apiError, toImportDocument, markReleaseDraftReleased,
  type NewsImportItem, type ReleaseDraftRef, type NewsInput, type NewsItem, type NewsBlock, type NewsCategory, type NewsRole, type NewsPlatform, type RouteInfo, type ReachEstimate,
} from '../../api/news'
import BlockEditor, { type EditorBlock } from './BlockEditor'
import NewsPreviews, { type PreviewItem } from './NewsPreviews'
import NewsImportDialog from './NewsImportDialog'
import { CATEGORIES, CATEGORY_KEYS, FALLBACK_APP_ROUTES, FALLBACK_PORTAL_ROUTES, ZONES, newKey } from './newsTheme'
import { wallToUtcIso, utcIsoToWall, formatWall, browserZone } from './newsTime'
import { stripListMarker, APP_VERSION_RE, RELEASE_VERSION_RE, MOD_KEY } from './inlineFormat'

interface Company { company_code: string; company_name: string; status?: string }

interface Form {
  title: string
  summary: string
  category: NewsCategory
  version: string
  blocks: EditorBlock[]
  cover_image_url: string
  audience: 'app' | 'portal' | 'both'
  companies_mode: 'all' | 'some'
  companies: string[]
  roles: NewsRole[]
  platforms: NewsPlatform[]
  min_app_version: string
  zone: string // 'company' or an IANA zone
  start: string // datetime-local wall clock in `zone`
  end: string
  pinned: boolean
  priority: 'normal' | 'high'
  push: boolean
  email_admins: boolean
}

const EMPTY: Form = {
  title: '', summary: '', category: 'release', version: '', blocks: [], cover_image_url: '',
  audience: 'both', companies_mode: 'all', companies: [], roles: ['employee', 'manager', 'admin'], platforms: ['ios', 'android', 'web'],
  min_app_version: '', zone: 'company', start: '', end: '', pinned: false, priority: 'normal', push: true, email_admins: false,
}

const ROLE_LABEL: Record<NewsRole, string> = { employee: 'Employees', manager: 'Managers', admin: 'Admins & owners' }
const PLATFORM_LABEL: Record<NewsPlatform, string> = { ios: 'iPhone', android: 'Android', web: 'Web / portal' }

function toEditor(blocks: NewsBlock[]): EditorBlock[] {
  return blocks.map((b) => ({ ...b, key: newKey() }) as EditorBlock)
}

/** Editor blocks -> API blocks: drops local keys, empty list lines and empty blocks. */
function cleanBlocks(blocks: EditorBlock[]): NewsBlock[] {
  const out: NewsBlock[] = []
  for (const b of blocks) {
    switch (b.type) {
      case 'heading': if (b.text.trim()) out.push({ type: 'heading', text: b.text.trim() }); break
      case 'paragraph': if (b.text.trim()) out.push({ type: 'paragraph', text: b.text.trim() }); break
      case 'list': {
        // The list draws its own numbers / bullets; drop any the author typed.
        const items = b.items.map((x) => stripListMarker(x, !!b.ordered).trim()).filter(Boolean)
        if (items.length) out.push(b.ordered ? { type: 'list', items, ordered: true } : { type: 'list', items })
        break
      }
      case 'callout': if (b.text.trim()) out.push({ type: 'callout', tone: b.tone, text: b.text.trim() }); break
      case 'image': if (b.url) out.push({ type: 'image', url: b.url, alt: b.alt.trim() }); break
      case 'button': if (b.label.trim()) out.push({ type: 'button', label: b.label.trim(), target: b.target }); break
    }
  }
  return out
}

function fromItem(it: NewsItem): Form {
  const company = it.tz_mode === 'company'
  const zone = company ? 'company' : (it.tz || 'UTC')
  return {
    title: it.title, summary: it.summary, category: it.category, version: it.version || '', blocks: toEditor(it.blocks),
    cover_image_url: it.cover_image_url || '', audience: it.audience, companies_mode: it.companies_mode, companies: it.companies || [],
    roles: it.roles.length ? it.roles : EMPTY.roles, platforms: it.platforms.length ? it.platforms : EMPTY.platforms,
    min_app_version: it.min_app_version || '', zone,
    start: company ? (it.starts_at || '') : utcIsoToWall(it.starts_at, zone),
    end: company ? (it.expires_at || '') : utcIsoToWall(it.expires_at, zone),
    pinned: it.pinned, priority: it.priority, push: it.push, email_admins: it.email_admins,
  }
}

function toInput(f: Form): NewsInput {
  const company = f.zone === 'company'
  return {
    title: f.title.trim(),
    summary: f.summary.trim(),
    category: f.category,
    version: (f.category === 'release' || f.category === 'fix') && f.version.trim() ? f.version.trim() : null,
    blocks: cleanBlocks(f.blocks),
    cover_image_url: f.cover_image_url || null,
    audience: f.audience,
    companies_mode: f.companies_mode,
    companies: f.companies,
    roles: f.roles,
    platforms: f.platforms,
    min_app_version: f.min_app_version.trim() || null,
    tz_mode: company ? 'company' : 'fixed',
    tz: company ? null : f.zone,
    starts_at: f.start ? (company ? f.start : wallToUtcIso(f.start, f.zone)) : null,
    expires_at: f.end ? (company ? f.end : wallToUtcIso(f.end, f.zone)) : null,
    pinned: f.pinned,
    priority: f.priority,
    push: f.push,
    email_admins: f.email_admins,
  }
}

/** A validated import (docs/news-api.md section 10) over the form: the
 *  fields it sets replace the form's, everything else is kept. */
function applyImport(f: Form, it: NewsImportItem): Form {
  const next = { ...f }
  if (it.title !== undefined) next.title = it.title
  if (it.summary !== undefined) next.summary = it.summary
  if (it.category !== undefined) next.category = it.category
  if (it.version !== undefined) next.version = it.version || ''
  if (it.blocks !== undefined) next.blocks = toEditor(it.blocks)
  if (it.cover_image_url !== undefined) next.cover_image_url = it.cover_image_url || ''
  if (it.audience !== undefined) next.audience = it.audience
  if (it.roles !== undefined) next.roles = it.roles
  if (it.platforms !== undefined) next.platforms = it.platforms
  if (it.pinned !== undefined) next.pinned = it.pinned
  if (it.priority !== undefined) next.priority = it.priority
  if (it.push !== undefined) next.push = it.push
  if (it.email_admins !== undefined) next.email_admins = it.email_admins
  return next
}

/** Is the start in the future (so Publish means Schedule)? Company time is
 *  judged in Eastern, the zone most companies are in. */
function startsLater(f: Form): boolean {
  if (!f.start) return false
  const iso = wallToUtcIso(f.start, f.zone === 'company' ? 'America/New_York' : f.zone)
  return !!iso && new Date(iso).getTime() > Date.now() + 60_000
}

function Panel({ title, right, children }: { title?: string; right?: ReactNode; children: ReactNode }) {
  return (
    <section className="bg-white border border-slate-200 rounded-xl">
      {title && (
        <div className="px-3.5 py-2.5 border-b border-slate-100 flex flex-wrap items-center gap-2">
          <h2 className="text-[13px] font-semibold uppercase tracking-wider text-slate-500">{title}</h2>
          {right && <div className="ml-auto flex flex-wrap gap-1.5">{right}</div>}
        </div>
      )}
      <div className="p-3.5 space-y-3">{children}</div>
    </section>
  )
}

function Seg<T extends string>({ value, options, onChange }: { value: T; options: Array<[T, string]>; onChange: (v: T) => void }) {
  return (
    <div className="inline-flex flex-wrap rounded-lg border border-slate-200 bg-slate-50 p-0.5">
      {options.map(([k, label]) => (
        <button key={k} type="button" aria-pressed={value === k} onClick={() => onChange(k)}
          className={`px-2.5 py-1 rounded-md text-xs font-semibold ${value === k ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>
          {label}
        </button>
      ))}
    </div>
  )
}

function Check({ checked, onChange, label, hint, icon }: { checked: boolean; onChange: (v: boolean) => void; label: string; hint?: string; icon?: ReactNode }) {
  return (
    <label className="flex gap-2 items-start text-sm text-slate-800">
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} className="mt-1 accent-blue-600" />
      <span>
        <span className="inline-flex items-center gap-1.5">{icon}{label}</span>
        {hint && <small className="block text-[11.5px] text-slate-400">{hint}</small>}
      </span>
    </label>
  )
}

const btn = 'inline-flex items-center gap-1.5 px-3 py-1.5 text-[13px] font-semibold rounded-lg border border-slate-300 bg-white hover:bg-slate-50 text-slate-800 disabled:opacity-50'
const btnPrimary = 'inline-flex items-center gap-1.5 px-3 py-1.5 text-[13px] font-semibold rounded-lg bg-blue-600 border border-blue-600 text-white hover:bg-blue-700 disabled:opacity-50'
const field = 'w-full text-sm rounded-lg border border-slate-200 px-2.5 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500'
const labelCls = 'block text-xs font-semibold text-slate-500 mb-1'

export default function NewsCompose() {
  const { id } = useParams()
  const navigate = useNavigate()
  const [form, setForm] = useState<Form>(EMPTY)
  const [item, setItem] = useState<NewsItem | null>(null)
  const [loading, setLoading] = useState(!!id)
  const [busy, setBusy] = useState<string | null>(null)
  const [toast, setToast] = useState<{ msg: string; tone: 'ok' | 'error' } | null>(null)
  const [companies, setCompanies] = useState<Company[]>([])
  const [companySearch, setCompanySearch] = useState('')
  const [routes, setRoutes] = useState<{ app: Record<string, RouteInfo>; portal: Record<string, RouteInfo> }>({ app: FALLBACK_APP_ROUTES, portal: FALLBACK_PORTAL_ROUTES })
  const [reach, setReach] = useState<ReachEstimate | null>(null)
  const [reachLoading, setReachLoading] = useState(false)
  const [aiOpen, setAiOpen] = useState(false)
  const [jsonDialog, setJsonDialog] = useState<{ mode: 'import' | 'export'; text?: string } | null>(null)
  const [ai, setAi] = useState({ notes: '', tone: 'friendly' as 'professional' | 'friendly' | 'brief' | 'detailed', length: 'medium' as 'short' | 'medium' | 'long', fromTitle: false })
  const toastTimer = useRef<number | undefined>(undefined)
  // Set when a draft is first created, so the URL change to /announcements/:id
  // doesn't reload it over the state (e.g. a publish) that follows.
  const justCreated = useRef<string | null>(null)
  // The release-news draft (file + hash) last imported from: publishing marks it
  // released so "From this release" stops offering it. A save doesn't.
  const importedDraft = useRef<ReleaseDraftRef | null>(null)

  const notify = useCallback((msg: string, tone: 'ok' | 'error' = 'ok') => {
    setToast({ msg, tone })
    window.clearTimeout(toastTimer.current)
    toastTimer.current = window.setTimeout(() => setToast(null), 3500)
  }, [])

  const set = <K extends keyof Form>(k: K, v: Form[K]) => setForm((f) => ({ ...f, [k]: v }))

  useEffect(() => {
    getNewsMeta().then((m) => setRoutes({ app: m.appRoutes, portal: m.portalRoutes })).catch(() => {})
    api.get('/super-admin/companies').then((r) => setCompanies((r.data?.companies || []) as Company[])).catch(() => {})
  }, [])

  useEffect(() => {
    if (!id) { setForm({ ...EMPTY }); setItem(null); setLoading(false); return }
    if (justCreated.current === id) { justCreated.current = null; return }
    setLoading(true)
    getNews(id)
      .then((it) => { setItem(it); setForm(fromItem(it)) })
      .catch((err) => notify(apiError(err, 'Could not load this announcement'), 'error'))
      .finally(() => setLoading(false))
  }, [id, notify])

  // Live reach, debounced. A half-typed app version ("4.0.") would be a 400,
  // so the count waits until the version is complete (or cleared).
  const minVersion = form.min_app_version.trim()
  const minVersionOk = !minVersion || APP_VERSION_RE.test(minVersion)
  const releaseVersion = form.version.trim()
  const releaseVersionOk = !releaseVersion || RELEASE_VERSION_RE.test(releaseVersion)
  const reachKey = JSON.stringify([form.companies_mode, form.companies, form.audience, form.roles, form.platforms, minVersionOk ? minVersion : null])
  useEffect(() => {
    if (!minVersionOk) return
    const t = window.setTimeout(() => {
      setReachLoading(true)
      estimateReach({
        companies_mode: form.companies_mode, companies: form.companies, audience: form.audience,
        roles: form.roles, platforms: form.platforms, min_app_version: minVersion || null,
      }).then(setReach).catch(() => setReach(null)).finally(() => setReachLoading(false))
    }, 600)
    return () => window.clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reachKey])

  const preview: PreviewItem = useMemo(() => ({
    id: item?.id || 'new', title: form.title, summary: form.summary, category: form.category,
    version: (form.category === 'release' || form.category === 'fix') && form.version.trim() ? form.version.trim() : null,
    blocks: cleanBlocks(form.blocks), cover_image_url: form.cover_image_url || null, pinned: form.pinned, priority: form.priority,
    audience: form.audience, unread: true,
    date: form.start ? formatWall(form.start).split(',')[0] : new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
  }), [form, item?.id])

  const save = async (): Promise<NewsItem | null> => {
    if (!form.title.trim()) { notify('Add a title first.', 'error'); return null }
    const input = toInput(form)
    const saved = item ? await updateNews(item.id, input) : await createNews(input)
    setItem(saved)
    if (!item) { justCreated.current = saved.id; navigate(`/announcements/${saved.id}`, { replace: true }) }
    return saved
  }

  const run = async (what: string, fn: () => Promise<void>) => {
    setBusy(what)
    try { await fn() } catch (err) { notify(apiError(err, 'Something went wrong'), 'error') } finally { setBusy(null) }
  }

  const onSave = () => run('save', async () => { if (await save()) notify(item?.status && item.status !== 'draft' ? 'Changes saved. They show everywhere now.' : 'Draft saved') })
  const onTest = () => run('test', async () => {
    const saved = await save()
    if (!saved) return
    const r = await sendTestNews(saved.id)
    notify(`Test push sent to your phone${r.companies.length ? ` (${r.companies.join(', ')})` : ''}`)
  })
  const later = startsLater(form)
  const onPublish = () => run('publish', async () => {
    const saved = await save()
    if (!saved) return
    const out = await publishNews(saved.id, !form.start)
    setItem(out)
    setForm(fromItem(out))
    let draftNote = ''
    const draft = importedDraft.current
    if (draft) {
      try {
        await markReleaseDraftReleased({ ...draft, newsId: out.id })
        importedDraft.current = null
      } catch (err) {
        // 501: tracking not set up on this server yet; nothing to remember.
        if ((err as { response?: { status?: number } })?.response?.status !== 501) draftNote = ` Could not mark ${draft.file} as released; use Mark released in Import.`
      }
    }
    notify((out.status === 'scheduled'
      ? `Scheduled. It shows ${formatWall(form.start)}${form.zone === 'company' ? ' company time' : ''}.`
      : 'Published') + draftNote)
  })
  const onUnpublish = () => run('unpublish', async () => {
    if (!item) return
    const out = await unpublishNews(item.id)
    setItem(out)
    notify('Moved back to drafts. It is hidden everywhere.')
  })
  const onDelete = () => run('delete', async () => {
    if (!item || !window.confirm('Delete this announcement everywhere? Reads and stats are deleted too.')) return
    await deleteNews(item.id)
    navigate('/announcements')
  })
  const onAi = () => run('ai', async () => {
    if (form.blocks.length && !window.confirm('Replace the current content with what AI writes?')) return
    const out = await aiWriteNews({ title: form.title, notes: ai.notes, category: form.category, audience: form.audience, tone: ai.tone, length: ai.length, fromTitle: ai.fromTitle })
    setForm((f) => ({ ...f, blocks: toEditor(out.blocks), summary: f.summary.trim() ? f.summary : out.summary }))
    setAiOpen(false)
    notify(out.source === 'ai' ? 'Written with AI. Check it before publishing.' : 'AI is not configured; drafted from your notes instead.')
  })
  const onImported = (it: NewsImportItem, ignored: string[], draft: ReleaseDraftRef | null) => {
    setForm((f) => applyImport(f, it))
    importedDraft.current = draft
    setJsonDialog(null)
    notify(ignored.length
      ? `Imported. Not imported: ${ignored.join(', ')}. Set companies and times here.`
      : 'Imported. Check it, choose who sees it, then save or publish.')
  }
  const onCopyJson = async () => {
    const text = JSON.stringify(toImportDocument(toInput(form)), null, 2)
    try {
      await navigator.clipboard.writeText(text)
      notify('Copied as JSON')
    } catch {
      setJsonDialog({ mode: 'export', text })
    }
  }
  const closeJsonDialog = useCallback(() => setJsonDialog(null), [])
  const upload = async (file: File) => {
    try { return (await uploadNewsImage(file)).url } catch (err) { throw new Error(apiError(err, 'Upload failed')) }
  }

  const status = item?.library_status || 'draft'
  const statusPill = {
    draft: 'bg-amber-50 text-amber-700', scheduled: 'bg-blue-50 text-blue-700', published: 'bg-emerald-50 text-emerald-700', expired: 'bg-slate-100 text-slate-600',
  }[status]
  const filteredCompanies = companies.filter((c) => !companySearch || `${c.company_name} ${c.company_code}`.toLowerCase().includes(companySearch.toLowerCase()))
  const zoneOptions = ZONES.some((z) => z.value === browserZone()) ? ZONES : [...ZONES, { value: browserZone(), label: `${browserZone()} (this computer)` }]
  const companiesText = form.companies_mode === 'all' ? 'All companies, including ones added later' : `${form.companies.length} selected ${form.companies.length === 1 ? 'company' : 'companies'}`
  const whereText = { both: 'employee app and admin portal', app: 'employee app', portal: 'admin portal' }[form.audience]

  if (loading) return <div className="p-8 text-sm text-slate-500">Loading…</div>

  return (
    <div className="p-4 sm:p-6 space-y-4 max-w-[1400px] mx-auto">
      <Link to="/announcements" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800"><ArrowLeftIcon className="w-4 h-4" />News</Link>

      <div className="bg-white border border-slate-200 rounded-xl px-4 py-3 flex flex-wrap items-center gap-2.5">
        <span className="w-8 h-8 rounded-lg bg-blue-50 grid place-items-center"><NewspaperIcon className="w-[18px] h-[18px] text-blue-600" /></span>
        <h1 className="text-[17px] font-semibold text-slate-900">{item ? 'Edit announcement' : 'New announcement'}</h1>
        <span className={`text-[11px] font-bold rounded-full px-2 py-0.5 ${statusPill}`}>{status[0].toUpperCase() + status.slice(1)}</span>
        {item?.legacy && <span className="text-[11px] font-bold rounded-full px-2 py-0.5 bg-slate-100 text-slate-500">Moved from old announcements</span>}
        <div className="ml-auto flex flex-wrap gap-2">
          {item && (status === 'published' || status === 'scheduled') && (
            <button type="button" className={btn} disabled={!!busy} onClick={onUnpublish}><EyeSlashIcon className="w-4 h-4" />Unpublish</button>
          )}
          {item && <button type="button" className={btn} disabled={!!busy} onClick={onDelete} aria-label="Delete"><TrashIcon className="w-4 h-4" /></button>}
          <button type="button" className={btn} disabled={!!busy} onClick={onSave}>{busy === 'save' ? 'Saving…' : item && item.status !== 'draft' ? 'Save changes' : 'Save draft'}</button>
          <button type="button" className={btn} disabled={!!busy} onClick={onTest}><DevicePhoneMobileIcon className="w-4 h-4" />{busy === 'test' ? 'Sending…' : 'Send test to me'}</button>
          {(!item || item.status === 'draft') && (
            <button type="button" className={btnPrimary} disabled={!!busy} onClick={onPublish}>{busy === 'publish' ? 'Working…' : later ? 'Schedule' : 'Publish'}</button>
          )}
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_420px]">
        <div className="space-y-4 min-w-0">
          <Panel>
            <input className="w-full text-[22px] font-bold text-slate-900 border-0 outline-none bg-transparent py-1 placeholder:text-slate-300" value={form.title} maxLength={120}
              onChange={(e) => set('title', e.target.value)} placeholder="Title" aria-label="Title" />
            <div>
              <label className={labelCls} htmlFor="news-summary">Summary <span className="font-medium text-slate-400">· shown in lists and push notifications</span></label>
              <textarea id="news-summary" className={`${field} resize-y min-h-[54px]`} maxLength={160} value={form.summary} onChange={(e) => set('summary', e.target.value.replace(/\n/g, ' '))} />
              <small className={`block text-[11.5px] mt-0.5 ${form.summary.length >= 150 ? 'text-amber-600' : 'text-slate-400'}`}>{form.summary.length}/160</small>
            </div>
            <div>
              <span className={labelCls}>Category</span>
              <div className="flex flex-wrap gap-1.5">
                {CATEGORY_KEYS.map((k) => {
                  const c = CATEGORIES[k]
                  const on = form.category === k
                  return (
                    <button key={k} type="button" aria-pressed={on} onClick={() => set('category', k)}
                      className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[12.5px] font-semibold border"
                      style={on ? { background: c.color, borderColor: 'transparent', color: '#fff' } : { borderColor: '#e2e8f0', color: '#475569', background: '#fff' }}>
                      <c.Icon className="w-3.5 h-3.5" style={{ color: on ? '#fff' : c.color }} />{c.label}
                    </button>
                  )
                })}
              </div>
              <small className="block text-[11.5px] text-slate-400 mt-1">Same name, colour and icon in the app, the admin portal and here.</small>
            </div>
            {(form.category === 'release' || form.category === 'fix') && (
              <div className="max-w-[200px]">
                <label className={labelCls} htmlFor="news-version">Version</label>
                <input id="news-version" className={field} value={form.version} maxLength={32} onChange={(e) => set('version', e.target.value)} placeholder="4.0.33"
                  aria-invalid={!releaseVersionOk} aria-describedby={releaseVersionOk ? undefined : 'news-version-hint'} />
                {!releaseVersionOk && <small id="news-version-hint" className="block text-[11.5px] text-red-600 mt-0.5">Use numbers like 4.0.29</small>}
              </div>
            )}
            <div>
              <span className={labelCls}>Cover image <span className="font-medium text-slate-400">· optional</span></span>
              <div className="flex flex-wrap gap-2 items-center">
                <label className={`${btn} cursor-pointer`}>
                  <PhotoIcon className="w-4 h-4" />{form.cover_image_url ? 'Replace' : 'Upload'}
                  <input type="file" accept="image/jpeg,image/png,image/webp,image/gif" className="hidden" onChange={async (e) => {
                    const f = e.target.files?.[0]; e.target.value = ''
                    if (!f) return
                    try { set('cover_image_url', await upload(f)) } catch (err) { notify(err instanceof Error ? err.message : 'Upload failed', 'error') }
                  }} />
                </label>
                {form.cover_image_url && (
                  <>
                    <img src={form.cover_image_url} alt="" className="h-10 w-16 object-cover rounded-md border border-slate-200" />
                    <button type="button" className="text-slate-400 hover:text-red-600" aria-label="Remove cover" onClick={() => set('cover_image_url', '')}><XMarkIcon className="w-4 h-4" /></button>
                  </>
                )}
              </div>
            </div>
          </Panel>

          <Panel title="Content" right={<>
            <button type="button" className={`${btn} !py-1 !text-xs`} onClick={() => setJsonDialog({ mode: 'import' })}><ArrowDownTrayIcon className="w-3.5 h-3.5" />Import</button>
            <button type="button" className={`${btn} !py-1 !text-xs`} onClick={onCopyJson}><ClipboardDocumentIcon className="w-3.5 h-3.5" />Copy as JSON</button>
            <button type="button" className={`${btn} !py-1 !text-xs`} onClick={() => setAiOpen((v) => !v)}><SparklesIcon className="w-3.5 h-3.5" />Write with AI</button>
          </>}>
            {aiOpen && (
              <div className="rounded-lg border border-blue-100 bg-blue-50/50 p-3 space-y-2">
                <textarea className={`${field} resize-y`} rows={3} value={ai.notes} disabled={ai.fromTitle} onChange={(e) => setAi({ ...ai, notes: e.target.value })}
                  placeholder="Notes: what changed, who it is for, what to do. AI writes it as blocks." />
                <div className="flex flex-wrap items-center gap-2">
                  <select className={`${field} !w-auto`} value={ai.tone} onChange={(e) => setAi({ ...ai, tone: e.target.value as typeof ai.tone })} aria-label="Tone">
                    <option value="friendly">Friendly</option><option value="professional">Professional</option><option value="brief">Brief</option><option value="detailed">Detailed</option>
                  </select>
                  <select className={`${field} !w-auto`} value={ai.length} onChange={(e) => setAi({ ...ai, length: e.target.value as typeof ai.length })} aria-label="Length">
                    <option value="short">Short</option><option value="medium">Medium</option><option value="long">Long</option>
                  </select>
                  <label className="inline-flex items-center gap-1.5 text-xs text-slate-600"><input type="checkbox" className="accent-blue-600" checked={ai.fromTitle} onChange={(e) => setAi({ ...ai, fromTitle: e.target.checked })} />From the title only</label>
                  <button type="button" className={`${btnPrimary} ml-auto`} disabled={busy === 'ai'} onClick={onAi}><SparklesIcon className="w-4 h-4" />{busy === 'ai' ? 'Writing…' : 'Write'}</button>
                </div>
              </div>
            )}
            <BlockEditor blocks={form.blocks} onChange={(b) => set('blocks', b)} appRoutes={routes.app} portalRoutes={routes.portal} onUpload={upload} />
            <small className="block text-[11.5px] text-slate-400 leading-relaxed">
              Select words and use{' '}
              <span className="inline-flex items-center gap-0.5 align-middle rounded border border-slate-200 bg-white px-1 text-slate-600"><BoldIcon className="w-3 h-3" aria-hidden="true" />Bold</span>{' '}
              ({MOD_KEY}+B) or{' '}
              <span className="inline-flex items-center gap-0.5 align-middle rounded border border-slate-200 bg-white px-1 text-slate-600"><LinkIcon className="w-3 h-3" aria-hidden="true" />Link</span>{' '}
              ({MOD_KEY}+K) in any text block. Lists number themselves. Content is saved as blocks, not HTML, so it looks right on phones, in dark mode and in the portal.
            </small>
          </Panel>

          <Panel title="Preview">
            <NewsPreviews item={preview} />
          </Panel>
        </div>

        <div className="space-y-4">
          <Panel title="Who sees it">
            <div>
              <span className={labelCls}>Companies</span>
              <Seg value={form.companies_mode} options={[['all', 'All companies'], ['some', 'Choose companies']]} onChange={(v) => set('companies_mode', v)} />
              {form.companies_mode === 'some' ? (
                <div className="mt-2 space-y-1.5">
                  <input className={field} value={companySearch} onChange={(e) => setCompanySearch(e.target.value)} placeholder="Search companies" aria-label="Search companies" />
                  <div className="max-h-48 overflow-auto grid grid-cols-1 sm:grid-cols-2 gap-1 pr-1">
                    {filteredCompanies.map((c) => (
                      <label key={c.company_code} className="flex gap-2 items-start text-[13px] text-slate-700">
                        <input type="checkbox" className="mt-0.5 accent-blue-600" checked={form.companies.includes(c.company_code)}
                          onChange={(e) => set('companies', e.target.checked ? [...form.companies, c.company_code] : form.companies.filter((x) => x !== c.company_code))} />
                        <span className="min-w-0 truncate">{c.company_name}</span>
                      </label>
                    ))}
                    {!filteredCompanies.length && <span className="text-xs text-slate-400">No companies match.</span>}
                  </div>
                </div>
              ) : (
                <small className="block text-[11.5px] text-slate-400 mt-1">Companies added later see it too. One announcement, not a copy per company.</small>
              )}
            </div>
            <div>
              <span className={labelCls}>Where</span>
              <Seg value={form.audience} options={[['both', 'App and portal'], ['app', 'Employee app'], ['portal', 'Admin portal']]} onChange={(v) => set('audience', v)} />
            </div>
            <div>
              <span className={labelCls}>Roles</span>
              <div className="flex flex-wrap gap-3">
                {(Object.keys(ROLE_LABEL) as NewsRole[]).map((r) => (
                  <label key={r} className="inline-flex items-center gap-1.5 text-[13px] text-slate-700">
                    <input type="checkbox" className="accent-blue-600" checked={form.roles.includes(r)}
                      onChange={(e) => set('roles', e.target.checked ? [...form.roles, r] : form.roles.filter((x) => x !== r))} />{ROLE_LABEL[r]}
                  </label>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2.5">
              <div>
                <label className={labelCls} htmlFor="news-minv">App version</label>
                <input id="news-minv" className={field} value={form.min_app_version} onChange={(e) => set('min_app_version', e.target.value)} placeholder="Any version"
                  aria-invalid={!minVersionOk} aria-describedby="news-minv-hint" />
                {minVersionOk
                  ? <small id="news-minv-hint" className="block text-[11.5px] text-slate-400 mt-0.5">or newer. Release notes for a feature only reach apps that have it.</small>
                  : <small id="news-minv-hint" className="block text-[11.5px] text-red-600 mt-0.5">Use numbers like 4.0.29</small>}
              </div>
              <div>
                <span className={labelCls}>Platforms</span>
                <div className="flex flex-col gap-0.5">
                  {(Object.keys(PLATFORM_LABEL) as NewsPlatform[]).map((p) => (
                    <label key={p} className="inline-flex items-center gap-1.5 text-[13px] text-slate-700">
                      <input type="checkbox" className="accent-blue-600" checked={form.platforms.includes(p)}
                        onChange={(e) => set('platforms', e.target.checked ? [...form.platforms, p] : form.platforms.filter((x) => x !== p))} />{PLATFORM_LABEL[p]}
                    </label>
                  ))}
                </div>
              </div>
            </div>
            {(!form.roles.length || !form.platforms.length) && <p className="text-xs text-red-600">Choose at least one role and one platform.</p>}
            <div className="text-[12.5px] text-slate-600 bg-slate-50 rounded-lg px-2.5 py-2">
              <b className="text-slate-900">{companiesText}</b> · {whereText} · {form.roles.length === 3 ? 'every role' : form.roles.map((r) => ROLE_LABEL[r].toLowerCase()).join(', ')}
              {minVersion ? ` · app ${minVersion}+` : ''} · {!minVersionOk ? 'finish the app version to count' : reachLoading && !reach ? 'counting…' : reach ? <>about <b className="text-slate-900">{reach.total.toLocaleString()}</b> {reach.total === 1 ? 'person' : 'people'}</> : 'reach unknown'}
              {reach && reach.unavailable.length > 0 && <span className="block text-[11px] text-slate-400">{reach.unavailable.length} company database(s) could not be counted.</span>}
            </div>
          </Panel>

          <Panel title="When">
            <div className="grid grid-cols-2 gap-2.5">
              <div>
                <label className={labelCls} htmlFor="news-start">Show from</label>
                <input id="news-start" type="datetime-local" className={field} value={form.start} onChange={(e) => set('start', e.target.value)} />
                <small className="block text-[11px] text-slate-400 mt-0.5">Empty: as soon as you publish.</small>
              </div>
              <div>
                <label className={labelCls} htmlFor="news-end">Hide after</label>
                <input id="news-end" type="datetime-local" className={field} value={form.end} onChange={(e) => set('end', e.target.value)} />
              </div>
            </div>
            <div>
              <label className={labelCls} htmlFor="news-tz">Time zone</label>
              <select id="news-tz" className={field} value={form.zone} onChange={(e) => {
                // Keep the same wall-clock digits; they now mean that time in the new zone.
                set('zone', e.target.value)
              }}>
                {zoneOptions.map((z) => <option key={z.value} value={z.value}>{z.label}</option>)}
              </select>
              <small className="block text-[11.5px] text-slate-400 mt-0.5">
                {form.zone === 'company'
                  ? `"Company time" shows it at ${form.start ? formatWall(form.start).split(', ').pop() : 'the chosen time'} in every company's own time zone.`
                  : 'Times above are in this zone. They are stored in UTC.'}
              </small>
            </div>
            <Check checked={form.pinned} onChange={(v) => set('pinned', v)} label="Pin to the top" hint="Stays first in the list until it expires." />
            <Check checked={form.priority === 'high'} onChange={(v) => set('priority', v ? 'high' : 'normal')} label="Show as a banner" hint="On the app home screen and the admin dashboard. For maintenance and anything urgent." />
          </Panel>

          <Panel title="Notify">
            <Check checked={form.push} onChange={(v) => set('push', v)} icon={<DevicePhoneMobileIcon className="w-4 h-4 text-slate-500" />} label="Push notification"
              hint={'Uses the title and summary. Tapping it opens this article. Respects quiet hours and each person\'s "Company and app news" setting.'} />
            <Check checked={form.email_admins} onChange={(v) => set('email_admins', v)} icon={<EnvelopeIcon className="w-4 h-4 text-slate-500" />} label="Email admins" hint="Admins and owners of the chosen companies." />
            {item && item.status !== 'draft' && <small className="block text-[11px] text-slate-400">Each company is notified once, when it goes live there. Changing these after that does not send again.</small>}
          </Panel>

          {item && item.status !== 'draft' && (
            <Panel title="Delivery">
              <div className="flex gap-5">
                <div><b className="block text-lg tabular-nums">{item.stats.reached.toLocaleString()}</b><span className="text-[11px] text-slate-500">Reached</span></div>
                <div><b className="block text-lg tabular-nums">{item.stats.reached ? Math.round((item.stats.opened / item.stats.reached) * 100) : 0}%</b><span className="text-[11px] text-slate-500">Opened</span></div>
                <div><b className="block text-lg tabular-nums">{item.stats.cta.toLocaleString()}</b><span className="text-[11px] text-slate-500">Tapped button</span></div>
              </div>
              {item.by_company.length > 0 && (
                <table className="w-full text-[12.5px]">
                  <thead><tr className="text-[11px] uppercase tracking-wider text-slate-400"><th className="text-left font-semibold py-1">Company</th><th className="text-right font-semibold">Reached</th><th className="text-right font-semibold">Opened</th><th className="text-right font-semibold">CTA</th></tr></thead>
                  <tbody>
                    {item.by_company.map((b) => (
                      <tr key={b.company_code} className="border-t border-slate-100">
                        <td className="py-1 pr-2 truncate max-w-[160px]">{b.company_name || b.company_code}</td>
                        <td className="text-right tabular-nums">{b.reached}</td>
                        <td className="text-right tabular-nums">{b.reached ? Math.round((b.opened / b.reached) * 100) : 0}%</td>
                        <td className="text-right tabular-nums">{b.cta}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              {item.sends.length > 0 && (
                <div className="text-[11.5px] text-slate-500 space-y-0.5">
                  {item.sends.map((s) => (
                    <div key={`${s.company_code}-${s.channel}`} className="flex gap-1.5">
                      <span className="font-semibold text-slate-700">{s.company_code}</span>
                      <span>{s.channel === 'push' ? 'push' : 'email'}: {s.status}{s.recipients ? `, ${s.sent}/${s.recipients}` : ''}</span>
                    </div>
                  ))}
                </div>
              )}
            </Panel>
          )}
        </div>
      </div>

      {jsonDialog && (
        <NewsImportDialog mode={jsonDialog.mode} exportText={jsonDialog.text} onClose={closeJsonDialog} onImport={onImported} />
      )}

      {toast && (
        <div role="status" className={`fixed left-1/2 bottom-6 -translate-x-1/2 z-50 px-3.5 py-2 rounded-lg text-sm text-white shadow-lg inline-flex items-center gap-2 ${toast.tone === 'ok' ? 'bg-slate-900' : 'bg-red-600'}`}>
          {toast.tone === 'ok' ? <CheckCircleIcon className="w-4 h-4" /> : <ExclamationTriangleIcon className="w-4 h-4" />}{toast.msg}
        </div>
      )}
    </div>
  )
}
